/** One hidden child keeps Tabulator's expand control without building keyword rows. */
export function keywordMapPlaceholderId(urlId: string | number | null | undefined) {
  return `km-placeholder-${urlId ?? ""}`;
}

export function isKeywordMapPlaceholder(row: { id?: string | number } | null | undefined) {
  return String(row?.id ?? "").startsWith("km-placeholder-");
}

/**
 * Stable 1-based index in the loaded keyword order.
 * The # column sorts on this value, so it does not follow the current visual position.
 * Rows do not need to declare row_num yet; the field is written onto each object.
 */
export function assignKeywordMapRowNumbers<T extends object>(
  rows: T[] | null | undefined
): Array<T & { row_num: number }> {
  const numbered = (rows || []) as Array<T & { row_num: number }>;
  numbered.forEach((row, index) => {
    row.row_num = index + 1;
  });
  return numbered;
}

/** Real keyword rows keyed by URL id. The list response stays intact for expand and export. */
export function keywordMapKeywordsByUrl(rows: any[] | null | undefined) {
  const keywords = new Map<string, any[]>();
  for (const row of rows || []) {
    const children = Array.isArray(row?.Childrens) ? row.Childrens : [];
    const real = children.filter((child: any) => !isKeywordMapPlaceholder(child));
    assignKeywordMapRowNumbers(real);
    keywords.set(String(row?.id), real);
  }
  return keywords;
}

/**
 * URL rows for the outer table. Each row keeps a single placeholder child so the
 * chevron stays, and the keyword objects are not copied into Tabulator's tree.
 */
export function keywordMapRowsForTable(rows: any[] | null | undefined) {
  return (rows || []).map((row) => ({
    ...row,
    Childrens: [{ id: keywordMapPlaceholderId(row?.id) }],
  }));
}

/** How many real keywords a URL already has. The placeholder does not count. */
export function keywordMapKeywordCount(
  keywordsByUrl: Map<string, any[]>,
  rowData: { id?: string | number } | null | undefined
) {
  if (!rowData) return 0;
  return keywordsByUrl.get(String(rowData.id))?.length || 0;
}

/** A URL stores at most this many keywords. */
export const KEYWORD_MAP_KEYWORD_LIMIT = 10;

/** True when another keyword still fits under the per-URL limit. */
export function keywordMapCanAddKeyword(count: number) {
  return count < KEYWORD_MAP_KEYWORD_LIMIT;
}

/**
 * Slots taken toward the limit.
 * The saved count wins when the nested table has not loaded yet.
 * An unsaved blank row counts once it is visible.
 */
export function keywordMapKeywordCountForAdd(stored: number, visible?: number | null) {
  const saved = Number.isFinite(stored) ? stored : 0;
  if (typeof visible !== "number" || !Number.isFinite(visible)) {
    return saved;
  }
  return Math.max(saved, visible);
}

/**
 * Search volume from last month's impressions, rounded to the nearest hundred.
 * Matches keyword map creation and the update-search-volume action.
 */
export function keywordMapSearchVolume(impressions: unknown): number {
  const value = typeof impressions === "number" ? impressions : Number(impressions);
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value / 100) * 100;
}

/** Writes search volume from each keyword's stored last-month impressions. */
export function keywordMapApplySearchVolumes(rows: any[] | null | undefined) {
  for (const row of rows || []) {
    const children = Array.isArray(row?.Childrens) ? row.Childrens : [];
    for (const child of children) {
      if (!child || isKeywordMapPlaceholder(child)) {
        continue;
      }
      child.search_volume = keywordMapSearchVolume(child.impressions);
    }
  }
}

/**
 * URL ids that stay expanded after the user collapses one row.
 * Collapsing removes that id. Keeping it would reopen the row on the next data load.
 */
export function keywordMapExpandedAfterCollapse(
  expanded: readonly unknown[] | null | undefined,
  collapsedId: unknown
): unknown[] {
  const id = String(collapsedId ?? "");
  return (Array.isArray(expanded) ? expanded : []).filter((value) => String(value) !== id);
}

/** Stable empty data prop so react-tabulator does not rebuild the table when rows arrive. */
export const KEYWORD_MAP_EMPTY_TABLE_DATA: any[] = [];

const URL_REFRESH_FIELDS = ["clicks", "impressions", "position", "pre_clicks", "pre_impressions", "data_up_at"] as const;
const KEYWORD_REFRESH_FIELDS = ["clicks", "impressions", "position", "ctr", "pre_clicks", "pre_impressions", "pre_position"] as const;

function refreshFieldPatch(source: Record<string, unknown> | null | undefined, fields: readonly string[]) {
  const patch: Record<string, unknown> = {};
  if (!source) {
    return patch;
  }
  for (const field of fields) {
    if (source[field] !== undefined) {
      patch[field] = source[field];
    }
  }
  return patch;
}

/**
 * Copies a URL refresh onto the loaded row and its stored keywords.
 * Keywords missing from the payload stay unchanged, and no new keyword row is created.
 */
export function keywordMapApplyUrlRefresh(row: any, payload: any) {
  if (!row || !payload || typeof payload !== "object") {
    return null;
  }
  const url = refreshFieldPatch(payload, URL_REFRESH_FIELDS);
  Object.assign(row, url);
  const incoming = Array.isArray(payload.Childrens) ? payload.Childrens : [];
  const byId = new Map<string, any>();
  for (const child of incoming) {
    if (child?.id == null || isKeywordMapPlaceholder(child)) {
      continue;
    }
    byId.set(String(child.id), child);
  }
  const keywords: Array<Record<string, unknown>> = [];
  const children = Array.isArray(row.Childrens) ? row.Childrens : [];
  for (const child of children) {
    if (isKeywordMapPlaceholder(child)) {
      continue;
    }
    const next = byId.get(String(child?.id));
    if (!next) {
      continue;
    }
    const patch = refreshFieldPatch(next, KEYWORD_REFRESH_FIELDS);
    Object.assign(child, patch);
    keywords.push({ id: child.id, ...patch });
  }
  return { url, keywords };
}
